import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const BASE_URL = '/api';

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [invite, setInvite] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | valid | invalid | done
  const [form, setForm] = useState({ username: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    fetch(`${BASE_URL}/auth/invite/${token}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => { setInvite(data); setStatus('valid'); })
      .catch(() => setStatus('invalid'));
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError('Şifreler eşleşmiyor'); return; }
    if (form.password.length < 6) { setError('Şifre en az 6 karakter olmalı'); return; }
    if (!form.username.trim()) { setError('Kullanıcı adı gerekli'); return; }

    setSaving(true);
    try {
      const res = await fetch(`${BASE_URL}/auth/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, username: form.username.trim(), password: form.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      localStorage.setItem('ozon_token', data.token);
      localStorage.setItem('ozon_username', data.username);
      setStatus('done');
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-600 rounded-2xl mb-4">
            <span className="text-2xl">📦</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Ozon WMS</h1>
          <p className="text-slate-400 text-sm mt-1">Hesap Oluştur</p>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-7">
          {status === 'loading' && (
            <div className="text-center py-4 text-slate-400">Davet doğrulanıyor...</div>
          )}

          {status === 'invalid' && (
            <div className="text-center">
              <div className="text-4xl mb-3">⛔</div>
              <h2 className="text-white font-semibold mb-2">Geçersiz Davet</h2>
              <p className="text-slate-400 text-sm">Bu davet bağlantısı geçersiz veya süresi dolmuş.</p>
              <button onClick={() => navigate('/login')} className="mt-4 text-blue-400 text-sm hover:underline">
                Giriş sayfasına git
              </button>
            </div>
          )}

          {status === 'done' && (
            <div className="text-center">
              <div className="text-4xl mb-3">✅</div>
              <h2 className="text-white font-semibold mb-2">Hesabınız Oluşturuldu</h2>
              <p className="text-slate-400 text-sm">Sisteme yönlendiriliyorsunuz...</p>
            </div>
          )}

          {status === 'valid' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-slate-700/50 rounded-lg px-4 py-3 text-sm">
                <div className="text-slate-400 text-xs mb-0.5">Davet edilen e-posta</div>
                <div className="text-white font-medium">{invite?.email}</div>
                <div className="text-xs mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    invite?.role === 'admin' ? 'bg-purple-900 text-purple-300' : 'bg-slate-600 text-slate-300'
                  }`}>
                    {invite?.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Kullanıcı Adı</label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={form.username}
                  onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                  placeholder="kullanici_adi"
                  className="w-full bg-slate-700 border border-slate-600 text-white placeholder-slate-500 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Şifre</label>
                <input
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="En az 6 karakter"
                  className="w-full bg-slate-700 border border-slate-600 text-white placeholder-slate-500 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Şifre Tekrar</label>
                <input
                  type="password"
                  required
                  value={form.confirm}
                  onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
                  placeholder="Şifreyi tekrar girin"
                  className="w-full bg-slate-700 border border-slate-600 text-white placeholder-slate-500 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {error && (
                <div className="bg-red-900/40 border border-red-800 text-red-400 text-sm px-3.5 py-2.5 rounded-lg">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium py-2.5 rounded-lg text-sm transition-colors"
              >
                {saving ? 'Oluşturuluyor...' : 'Hesabı Oluştur'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
